import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const DigitalTransformation = () => {
  return (
    <>
      <SEO 
        title="Digital Transformation Services - Modernize Your Business"
        description="Transform your business with enterprise-grade digital transformation services. Abacies Logiciels delivers strategic planning, technology modernization, and organizational change management to drive innovation and growth."
        keywords="digital transformation, business modernization, enterprise transformation, digital strategy, technology adoption, change management, digital innovation"
      />

      {/* Hero Section */}
      <section className="hero-section py-10">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              Digital <span className="text-gradient">Transformation</span> Services
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up mb-8">
              Navigate the complexities of digital transformation with strategic guidance and proven methodologies. We help enterprises modernize operations, enhance customer experiences, and unlock new revenue streams through comprehensive digital transformation programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Start Your Transformation
              </Link>
              <button className="btn-secondary">
                Download Framework Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Why Digital Transformation Matters</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              In today's rapidly evolving business landscape, digital transformation is no longer optional—it's essential for staying competitive and relevant
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                title: 'Accelerated Growth',
                description: 'Drive revenue growth through digital channels, new business models, and enhanced customer engagement'
              },
              {
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                title: 'Cost Optimization',
                description: 'Reduce operational costs through automation, streamlined processes, and efficient resource utilization'
              },
              {
                icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                title: 'Enhanced Experience',
                description: 'Deliver exceptional customer and employee experiences through intuitive digital interfaces and personalization'
              },
              {
                icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                title: 'Competitive Advantage',
                description: 'Stay ahead of competition with innovative solutions, faster time-to-market, and data-driven decision making'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-blush-light/40 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-blush-light/60 transition-all duration-300">
                  <svg className="w-10 h-10 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{benefit.title}</h3>
                <p className="text-titanium">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-title">Our Transformation Methodology</h2>
            <p className="section-subtitle">
              A proven, phased approach that ensures successful digital transformation with minimal disruption to your operations
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Discovery & Assessment',
                  description: 'We analyze your current state, identify opportunities, assess technology readiness, and define clear transformation objectives aligned with business goals.',
                  deliverables: ['Digital Maturity Assessment', 'Gap Analysis Report', 'Stakeholder Interviews', 'Quick Win Identification']
                },
                {
                  phase: 'Phase 2',
                  title: 'Strategy & Planning',
                  description: 'Develop a comprehensive roadmap with prioritized initiatives, technology architecture, change management plans, and measurable KPIs.',
                  deliverables: ['Transformation Roadmap', 'Technology Blueprint', 'ROI Projections', 'Risk Mitigation Plan']
                },
                {
                  phase: 'Phase 3',
                  title: 'Implementation & Execution',
                  description: 'Execute transformation initiatives through agile methodology, with continuous stakeholder engagement and iterative improvements.',
                  deliverables: ['Agile Sprints', 'System Integration', 'Process Automation', 'Change Management Activities']
                }
              ].map((phase, index) => (
                <div key={index} className="card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-red text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-primary-red font-semibold mb-1">{phase.phase}</div>
                      <h3 className="text-xl font-bold mb-2 text-charcoal">{phase.title}</h3>
                      <p className="text-titanium mb-4">{phase.description}</p>
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-charcoal">Key Deliverables:</div>
                        <ul className="grid grid-cols-2 gap-2">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-start text-sm text-titanium">
                              <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {[
                {
                  phase: 'Phase 4',
                  title: 'Optimization & Scaling',
                  description: 'Fine-tune implementations based on performance metrics, expand successful initiatives, and drive continuous improvement across the organization.',
                  deliverables: ['Performance Optimization', 'Scaling Framework', 'Training Programs', 'Knowledge Transfer']
                },
                {
                  phase: 'Phase 5',
                  title: 'Sustainment & Evolution',
                  description: 'Ensure long-term success through ongoing support, monitoring, and adaptation to emerging technologies and changing business needs.',
                  deliverables: ['24/7 Support Services', 'Performance Monitoring', 'Innovation Pipeline', 'Strategic Reviews']
                }
              ].map((phase, index) => (
                <div key={index} className="card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-red text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                      {index + 4}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-primary-red font-semibold mb-1">{phase.phase}</div>
                      <h3 className="text-xl font-bold mb-2 text-charcoal">{phase.title}</h3>
                      <p className="text-titanium mb-4">{phase.description}</p>
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-charcoal">Key Deliverables:</div>
                        <ul className="grid grid-cols-2 gap-2">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-start text-sm text-titanium">
                              <svg className="w-4 h-4 text-primary-red mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Success Metrics */}
              <div className="card bg-gradient-to-br from-primary-red to-deep-garnet text-white">
                <h3 className="text-xl font-bold mb-4">Transformation Success Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold mb-1">85%</div>
                    <div className="text-sm opacity-90">Average ROI Increase</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1">60%</div>
                    <div className="text-sm opacity-90">Cost Reduction</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1">3x</div>
                    <div className="text-sm opacity-90">Faster Time-to-Market</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1">95%</div>
                    <div className="text-sm opacity-90">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Transformation Service Areas</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive solutions covering all aspects of digital transformation
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Business Process Transformation',
                description: 'Redesign and optimize end-to-end business processes through automation, workflow optimization, and digital enablement',
                features: ['Process Mining & Analysis', 'RPA Implementation', 'Workflow Automation', 'Process Standardization']
              },
              {
                title: 'Customer Experience Transformation',
                description: 'Create seamless omnichannel experiences that delight customers and drive loyalty across all touchpoints',
                features: ['Journey Mapping', 'Personalization Engines', 'Mobile-First Design', 'Customer Analytics']
              },
              {
                title: 'Data & Analytics Transformation',
                description: 'Build data-driven organizations with advanced analytics capabilities, real-time insights, and predictive intelligence',
                features: ['Data Architecture', 'BI Implementation', 'ML & AI Integration', 'Data Governance']
              },
              {
                title: 'Technology Modernization',
                description: 'Upgrade legacy systems to modern, cloud-native architectures with microservices and API-first design',
                features: ['Cloud Migration', 'Application Modernization', 'API Development', 'DevOps Implementation']
              },
              {
                title: 'Organizational Transformation',
                description: 'Transform culture, structure, and operating models to support digital-first ways of working',
                features: ['Change Management', 'Agile Adoption', 'Digital Skills Training', 'Leadership Development']
              },
              {
                title: 'Innovation & Growth',
                description: 'Identify and capitalize on new digital opportunities through innovation frameworks and rapid experimentation',
                features: ['Innovation Labs', 'MVP Development', 'Digital Product Design', 'Growth Hacking']
              }
            ].map((area, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{area.title}</h3>
                <p className="text-titanium mb-4">{area.description}</p>
                <div className="space-y-2">
                  {area.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-graphite">
                      <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Industry-Specific Transformations</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Tailored transformation strategies designed for your industry's unique challenges and opportunities
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                industry: 'Healthcare',
                challenge: 'Improve patient outcomes while reducing costs',
                solution: 'Digital health platforms, telemedicine integration, EHR modernization, and AI-powered diagnostics',
                link: '/solutions/healthcare-life-sciences'
              },
              {
                industry: 'Financial Services',
                challenge: 'Deliver personalized services and ensure compliance',
                solution: 'Digital banking platforms, automated compliance, blockchain integration, and AI-driven insights',
                link: '/solutions/financial-services'
              },
              {
                industry: 'Manufacturing',
                challenge: 'Optimize operations and enable smart factories',
                solution: 'IoT integration, predictive maintenance, supply chain digitization, and quality automation',
                link: '/solutions/manufacturing-logistics'
              },
              {
                industry: 'Retail & E-commerce',
                challenge: 'Create seamless omnichannel experiences',
                solution: 'E-commerce platforms, inventory optimization, personalization engines, and contactless payments',
                link: '/solutions/retail-ecommerce'
              }
            ].map((item, index) => (
              <div key={index} className="card">
                <h3 className="text-xl font-bold mb-3 text-charcoal">{item.industry}</h3>
                <div className="mb-3">
                  <div className="text-sm font-semibold text-graphite mb-1">Challenge:</div>
                  <p className="text-titanium">{item.challenge}</p>
                </div>
                <div className="mb-4">
                  <div className="text-sm font-semibold text-graphite mb-1">Our Solution:</div>
                  <p className="text-titanium">{item.solution}</p>
                </div>
                <Link to={item.link} className="text-primary-red hover:underline font-semibold">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Transformation Technology Stack</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Leveraging cutting-edge technologies to power your digital transformation
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                category: 'Cloud Platforms',
                technologies: ['AWS', 'Microsoft Azure', 'Google Cloud', 'IBM Cloud']
              },
              {
                category: 'Integration',
                technologies: ['MuleSoft', 'Dell Boomi', 'Apache Kafka', 'API Gateway']
              },
              {
                category: 'Data & Analytics',
                technologies: ['Snowflake', 'Databricks', 'Power BI', 'Tableau']
              },
              {
                category: 'Automation',
                technologies: ['UiPath', 'Automation Anywhere', 'Blue Prism', 'Microsoft Power Automate']
              }
            ].map((stack, index) => (
              <div key={index} className="card text-center">
                <h3 className="text-lg font-semibold mb-4 text-charcoal">{stack.category}</h3>
                <ul className="space-y-2">
                  {stack.technologies.map((tech, idx) => (
                    <li key={idx} className="text-sm text-titanium">{tech}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Preview */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="card bg-white">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="text-sm text-primary-red font-semibold mb-2">SUCCESS STORY</div>
                  <h2 className="text-3xl font-bold mb-4 text-charcoal">
                    Global Retailer Achieves 300% ROI Through Digital Transformation
                  </h2>
                  <p className="text-titanium mb-6">
                    A Fortune 500 retail company partnered with us to transform their operations, resulting in significant improvements in efficiency, customer satisfaction, and revenue growth within 18 months.
                  </p>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                      <div className="text-2xl font-bold text-primary-red">300%</div>
                      <div className="text-sm text-titanium">ROI Achieved</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-primary-red">65%</div>
                      <div className="text-sm text-titanium">Cost Reduction</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-primary-red">45%</div>
                      <div className="text-sm text-titanium">Faster Delivery</div>
                    </div>
                  </div>
                  <button className="btn-secondary">View Full Case Study</button>
                </div>
                <div className="bg-alabaster rounded-xl p-8 h-full flex items-center justify-center">
                  <svg className="w-48 h-48 text-cool-grey/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-3xl mx-auto">
            <h2 className="section-title text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {[
                {
                  question: 'How long does a typical digital transformation project take?',
                  answer: 'Digital transformation timelines vary based on scope and complexity. Quick wins can be achieved in 3-6 months, while comprehensive enterprise-wide transformations typically take 18-36 months. We follow a phased approach to deliver value early and continuously.'
                },
                {
                  question: 'What is the typical ROI of digital transformation?',
                  answer: 'Our clients typically see ROI within 12-18 months, with benefits including 40-60% cost reduction in targeted areas, 2-3x improvement in operational efficiency, and 20-40% revenue growth through new digital channels. Actual ROI depends on the scope and execution quality.'
                },
                {
                  question: 'How do you handle change management?',
                  answer: 'Change management is integrated throughout our transformation methodology. We work closely with leadership, develop communication plans, provide comprehensive training, establish change champions, and monitor adoption metrics to ensure successful organizational change.'
                },
                {
                  question: 'What industries do you specialize in?',
                  answer: 'We have deep expertise across healthcare, financial services, manufacturing, retail, government, and education sectors. Our team includes industry specialists who understand sector-specific regulations, challenges, and opportunities.'
                }
              ].map((faq, index) => (
                <div key={index} className="card">
                  <h3 className="text-lg font-semibold mb-3 text-charcoal">{faq.question}</h3>
                  <p className="text-titanium">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Let's discuss how our proven transformation methodology can help you achieve your digital ambitions and drive sustainable growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Consultation
            </Link>
            <Link to="/services" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DigitalTransformation;